import ClassNames from "classnames";
import { FunctionComponent } from "react";
import { getActiveSite } from "../../selectors/sites.js";
import { useAppSelector } from "../../store/hooks.js";
import buttonStyles from "../../stylesheets/components/button.module.css";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import styles from "../../stylesheets/components/text-module.module.css";
import {
  BaseModuleProps,
  EmbeddedGoogleMapsModuleSettings,
  ImageModuleSettings,
  TextModuleSettings,
  TranslatedModule,
} from "../../types/index.js";
import { css } from "../../utils/emotion.js";
import {
  getActiveColorScheme,
  getModulesByParentId,
  getTranslatedButtonModules,
  getTranslatedModule,
} from "../../utils/utils.js";
import ModuleHeadings from "../ModuleHeadings.js";
import RichEditorWrapper from "../RichEditorWrapper.js";
import Typography from "../Typography.js";
import TypographyContent from "../TypographyContent.js";
import TypographyWrapper from "../TypographyWrapper.js";
import TypographyWrapperInner from "../TypographyWrapperInner.js";
import ButtonModule from "./ButtonModule.js";
import EmbeddedGoogleMapsModule from "./EmbeddedGoogleMapsModule.js";
import ImageModule from "./ImageModule.js";

interface Props extends BaseModuleProps<TextModuleSettings> {}

const TextModule: FunctionComponent<Props> = ({
  translatedModule,
  translatedModule: {
    settings: {
      textAlign,
      mediaAspectRatio,
      mediaAlign,
      width,
      boxAlign,
      columns,
    },
    id,
    translation: {
      languageId,
      settings: { subtitle, title },
    },
  },
  isPreview,
  isActive,
  activeModuleId,
  pageId,
  isFirstOnPage,
}) => {
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(
      state.colorSchemes,
      getActiveSite(state.sites),
      translatedModule,
    ),
  );
  const modules = useAppSelector((state) => state.modules);
  const buttons = getTranslatedButtonModules({
    modules,
    moduleId: id,
    languageId,
    pageId,
  });
  const media = getModulesByParentId<
    ImageModuleSettings | EmbeddedGoogleMapsModuleSettings
  >(modules, id, pageId)
    .filter(
      ({ type }) =>
        type === "ImageModule" || type === "EmbeddedGoogleMapsModule",
    )
    .map((module) => getTranslatedModule(module, languageId))[0];

  const isImageMedia = media && checkIsImageModule(media);
  const isMapMedia = media && checkIsEmbeddedGoogleMapsModule(media);

  return (
    <Typography
      title={title}
      subtitle={subtitle}
      id={id}
      colors={{ background: scheme.main.background }}
    >
      <TypographyWrapper
        className={ClassNames(
          moduleBaseStyles.Module__Wrapper,
          moduleBaseStyles["Module__Wrapper--default-padding"],
          `FlexAlignH--${boxAlign}`,
        )}
      >
        <TypographyWrapperInner
          width={width}
          mediaAlign={mediaAlign}
          mediaType={isImageMedia ? "image" : isMapMedia ? "map" : undefined}
          className={ClassNames({
            [styles["TextModule__WrapperInner--media-right"]]:
              mediaAlign === "right",
          })}
        >
          {isImageMedia && (
            <div>
              <ImageModule
                aspectRatio={mediaAspectRatio}
                lazyLoad={true}
                translatedModule={media}
                width={600}
                sizes="(min-width: 640px) 40vw, (min-width: 1600px) 600px, 100vw"
                isPreview={isPreview}
                isSlider={false}
                pageId={pageId}
                scheme={scheme}
                showOverlay={false}
              />
            </div>
          )}

          {isMapMedia && (
            <div>
              <EmbeddedGoogleMapsModule
                aspectRatio={mediaAspectRatio}
                isActive={isActive || media.id === activeModuleId}
                isPreview={isPreview}
                translatedModule={media}
              />
            </div>
          )}

          <TypographyContent
            className={ClassNames(
              styles.TextModule__Content,
              css({ color: scheme.main.text }),
            )}
          >
            <div>
              <ModuleHeadings
                scheme={scheme}
                isFirstOnPage={isFirstOnPage}
                textAlign={textAlign}
                title={title}
                subtitle={subtitle}
              />

              <RichEditorWrapper
                className={ClassNames(`TextAlign--${textAlign.description}`, {
                  [styles["TextModule__Description--columns-2"]]: columns === 2,
                })}
                module={translatedModule}
                pageId={pageId}
                languageId={languageId}
                isPreview={isPreview}
                scheme={scheme}
              />
            </div>
            {buttons.length > 0 && (
              <div
                className={ClassNames(
                  buttonStyles.ButtonGroup,
                  textAlign.buttons !== "justify"
                    ? buttonStyles[`ButtonGroup--align-${textAlign.buttons}`]
                    : undefined,
                )}
              >
                {buttons.map((button) => (
                  <ButtonModule
                    key={button.id}
                    scheme={scheme}
                    isPreview={isPreview}
                    translatedModule={button}
                  />
                ))}
              </div>
            )}
          </TypographyContent>
        </TypographyWrapperInner>
      </TypographyWrapper>
    </Typography>
  );
};

const checkIsImageModule = (
  module: TranslatedModule,
): module is TranslatedModule<ImageModuleSettings> =>
  module.type === "ImageModule";

const checkIsEmbeddedGoogleMapsModule = (
  module: TranslatedModule,
): module is TranslatedModule<EmbeddedGoogleMapsModuleSettings> =>
  module.type === "EmbeddedGoogleMapsModule";

export default TextModule;
